import { useMemo } from 'react';

import { getIsNotFound } from 'src/components/not-found/get-is-not-found';
import { Apollo } from 'src/gql/generated';

/**
 * Chat history lists direct chats only. Threads started from a task or workflow live in that
 * activity feed, so they would otherwise show up twice. Every read and cache write of
 * `GetAgentThreads` must pass these variables, or it addresses a different cache entry.
 */
export const RECENT_CHAT_THREADS_VARIABLES = {
  filter: Apollo.AgentThreadHistoryFilter.Direct,
} satisfies Apollo.GetAgentThreadsQueryVariables;

export function useRecentChatThreads(
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetAgentThreadsQuery>[0]>, 'variables'>
) {
  const {
    data: recentThreadsData,
    loading,
    refetch,
  } = Apollo.useGetAgentThreadsQuery({
    ...options,
    variables: RECENT_CHAT_THREADS_VARIABLES,
  });

  return {
    loading,
    recentThreads: recentThreadsData?.agentThreads ?? [],
    refetch,
  };
}

export function useGetChatThread(
  threadId: UUID | null,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetAgentThreadQuery>[0]>, 'variables'>
) {
  const {
    data: threadData,
    error,
    loading: threadLoading,
  } = Apollo.useGetAgentThreadQuery({
    ...options,
    skip: !threadId,
    variables: { id: threadId ?? '' },
  });
  const messages = useMemo(
    () => threadData?.agentThread?.messages ?? [],
    [threadData?.agentThread?.messages]
  );
  /**
   * Gated on `threadId` because a skipped query looks exactly like a missing
   * thread — no data, no error — and the composer with no thread selected is
   * the new-chat state, not a not-found one.
   */
  const isNotFound =
    Boolean(threadId) &&
    getIsNotFound({ entity: threadData?.agentThread, error, loading: threadLoading });

  return {
    /**
     * A load that failed for any other reason: an outage or a network drop. Gated
     * on the entity still being absent because `errorPolicy: 'all'` can return a
     * cached thread alongside a background error — that's a readable chat, not a
     * failure to show. Separate from `isNotFound` because a dead thread is final
     * and this one is worth retrying.
     */
    hasLoadError: Boolean(error) && !isNotFound && !threadData?.agentThread,
    isNotFound,
    loading: threadLoading,
    messages,
    thread: threadData?.agentThread,
  };
}
